'use client';

import { createContext, useCallback, useContext, useState } from 'react';

type ToastMessage = {
  id: number;
  message: string;
  duration?: number;
};

type ToastOptions = {
  duration?: number;
};

type ToastContextValue = {
  showToast: (message: string, options?: ToastOptions) => void;
  toasts: ToastMessage[];
  dismiss: (id: number) => void;
  clearAll: () => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((message: string, options?: ToastOptions) => {
    const id = ++nextId;
    setToasts((prev) => [
      ...prev,
      { id, message, duration: options?.duration },
    ]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, toasts, dismiss, clearAll }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast(): (message: string, options?: ToastOptions) => void {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx.showToast;
}

export function useToastState(): Pick<
  ToastContextValue,
  'toasts' | 'dismiss' | 'clearAll'
> {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToastState must be used within ToastProvider');
  }
  return { toasts: ctx.toasts, dismiss: ctx.dismiss, clearAll: ctx.clearAll };
}
